import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic meta tags on each page
 * Includes JSON-LD structured data for rich snippets
 * Usage: <SEO title="Page Title" description="Page description" />
 */
const SEO = ({ 
  title = 'SQUIDEMY - Master IT Skills with Hands-On Tech Courses',
  description = 'Build real-world IT skills through hands-on labs, certification prep, and expert-led courses. From cybersecurity to cloud computing, launch or advance your IT career today.',
  keywords = 'IT training, cybersecurity courses, CompTIA certification, cloud computing, hands-on labs',
  image = 'https://squidemy.com/squiddy_512.png',
  url = 'https://squidemy.com',
  type = 'website',
  author = 'Squidemy',
  publishedTime = null,
  modifiedTime = null,
  section = null,
  tags = [],
  noindex = false,
  // Structured data props
  structuredData = null,
  pageType = 'WebPage', // WebPage, Course, Article, FAQPage, Organization
  cardSize = 'large' // 'large' for summary_large_image, 'small' for summary (smaller preview)
}) => {
  const fullTitle = title.includes('SQUIDEMY') ? title : `${title} | SQUIDEMY`;
  
  // Organization structured data (always included)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Squidemy",
    "alternateName": "SQUIDEMY",
    "url": "https://squidemy.com",
    "logo": "https://squidemy.com/squiddy_512.png",
    "sameAs": [
      "https://www.facebook.com/squidemy",
      "https://twitter.com/squidemy",
      "https://www.linkedin.com/company/squidemy",
      "https://www.youtube.com/@squidemy",
      "https://www.instagram.com/squidemy",
      "https://www.tiktok.com/@squidemy",
      "https://discord.gg/squidemy"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "",
      "contactType": "customer support",
      "email": "support@squidemy.com",
      "availableLanguage": ["English"]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "200 N Vineyard Blvd",
      "addressLocality": "Honolulu",
      "addressRegion": "HI",
      "postalCode": "96817",
      "addressCountry": "US"
    }
  };

  // WebSite structured data for search box
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Squidemy",
    "url": "https://squidemy.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://squidemy.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Educational Organization schema
  const educationalOrgSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Squidemy",
    "url": "https://squidemy.com",
    "description": "Build real-world IT skills through hands-on labs, certification prep, and expert-led courses.",
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "IT Training Courses",
      "itemListElement": [
        {
          "@type": "Course",
          "name": "CompTIA Certification Prep",
          "description": "Prepare for CompTIA A+, Network+, Security+ certifications",
          "provider": {
            "@type": "Organization",
            "name": "Squidemy"
          }
        },
        {
          "@type": "Course", 
          "name": "Cybersecurity Training",
          "description": "Hands-on cybersecurity courses and labs",
          "provider": {
            "@type": "Organization",
            "name": "Squidemy"
          }
        },
        {
          "@type": "Course",
          "name": "Cloud Computing",
          "description": "AWS, Azure, and GCP certification preparation",
          "provider": {
            "@type": "Organization",
            "name": "Squidemy"
          }
        }
      ]
    }
  };

  // Default page schema
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": pageType,
    "name": fullTitle,
    "description": description,
    "url": url,
    "image": image,
    "publisher": {
      "@type": "Organization",
      "name": "Squidemy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://squidemy.com/squiddy_512.png"
      }
    }
  };

  // Article schema for blog posts
  const articleSchema = type === 'article' ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Squidemy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://squidemy.com/squiddy_512.png"
      }
    },
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  } : null;

  // Breadcrumb schema (basic)
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://squidemy.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": title.replace(' | SQUIDEMY', ''),
        "item": url
      }
    ]
  };
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Squidemy" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={cardSize === 'small' ? 'summary' : 'summary_large_image'} />
      <meta name="twitter:site" content="@squidemy" />
      <meta name="twitter:creator" content="@squidemy" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Article specific (for blog posts) */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && section && (
        <meta property="article:section" content={section} />
      )}
      {type === 'article' && tags.length > 0 && 
        tags.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))
      }
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(educationalOrgSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(pageSchema)}
      </script>
      {url !== 'https://squidemy.com' && url !== 'https://squidemy.com/' && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Pre-configured SEO for all pages
export const PageSEO = {
  // Main Pages
  home: {
    title: 'SQUIDEMY - Master IT Skills with Hands-On Tech Courses',
    description: 'Build real-world IT skills through hands-on labs, certification prep, and expert-led courses. From cybersecurity to cloud computing, launch or advance your IT career today.',
    keywords: 'IT training, cybersecurity courses, CompTIA certification, cloud computing, AWS training, Azure certification, hands-on labs, tech education'
  },
  about: {
    title: 'About Squidemy - Our Mission & Team',
    description: 'Learn about Squidemy\'s mission to make IT education accessible to everyone. Meet our team and discover our story.',
    keywords: 'about squidemy, IT education, tech training company, online learning platform'
  },
  contact: {
    title: 'Contact Us - Get in Touch',
    description: 'Have questions? Contact the Squidemy team. We\'re here to help with course inquiries, support, and partnership opportunities.',
    keywords: 'contact squidemy, support, help, questions, partnership'
  },
  
  // Learning & Courses
  courses: {
    title: 'IT Courses & Training Programs',
    description: 'Explore our comprehensive IT courses covering cybersecurity, cloud computing, networking, and more. Get certified with CompTIA, AWS, and Azure prep courses.',
    keywords: 'IT courses, online courses, cybersecurity training, cloud certification, networking courses, tech skills'
  },
  courseDetail: {
    title: 'Course Details',
    description: 'Learn more about this IT training course. View curriculum, requirements, and enroll today.',
    keywords: 'IT course, online learning, tech training, certification course'
  },
  bootcamps: {
    title: 'IT Bootcamps - Intensive Training Programs',
    description: 'Accelerate your IT career with intensive bootcamp programs. Learn job-ready skills in weeks, not years. Cybersecurity, cloud, and development tracks available.',
    keywords: 'IT bootcamp, coding bootcamp, cybersecurity bootcamp, cloud bootcamp, career change, tech training'
  },
  bootcampDetail: {
    title: 'Bootcamp Details',
    description: 'Explore this intensive IT bootcamp program. View schedule, curriculum, and career outcomes.',
    keywords: 'bootcamp details, IT training, intensive program, career bootcamp'
  },
  labs: {
    title: 'Hands-On Labs - Practice Real IT Skills',
    description: 'Get hands-on experience with real IT environments. Practice cybersecurity, networking, and cloud computing in our virtual labs.',
    keywords: 'hands-on labs, virtual labs, IT practice, cybersecurity labs, networking labs, cloud labs'
  },
  certPrep: {
    title: 'Certification Prep - Pass Your IT Exams',
    description: 'Prepare for IT certifications with our exam prep courses. CompTIA A+, Network+, Security+, AWS, Azure, and more. Practice tests and study guides included.',
    keywords: 'certification prep, CompTIA A+, CompTIA Network+, CompTIA Security+, AWS certification, Azure certification, exam prep'
  },
  certificates: {
    title: 'My Certificates - Your Achievements',
    description: 'View and download your earned certificates. Share your accomplishments with employers and on social media.',
    keywords: 'certificates, achievements, credentials, IT certification, digital badges'
  },
  flashcards: {
    title: 'SquidCards - Flashcard Study Tool',
    description: 'Study IT concepts with interactive flashcards. Perfect for certification prep and reinforcing knowledge.',
    keywords: 'flashcards, study tool, IT learning, certification prep, memorization'
  },
  studyAssistant: {
    title: 'SquidStudy - AI Study Assistant',
    description: 'Get personalized study help from our AI assistant. Ask questions, get explanations, and accelerate your learning.',
    keywords: 'AI study assistant, learning help, IT tutoring, study aid'
  },
  roadmap: {
    title: 'Career Roadmap - Plan Your IT Journey',
    description: 'Create your personalized IT career roadmap. Discover learning paths for different tech careers.',
    keywords: 'career roadmap, learning path, IT career, tech career planning'
  },
  
  // Interactive Features
  ctf: {
    title: 'Capture The Squid - Cybersecurity Challenges',
    description: 'Test your cybersecurity skills with Capture The Flag challenges. Compete with others, earn points, and climb the leaderboard.',
    keywords: 'CTF, capture the flag, cybersecurity challenges, hacking challenges, security competition'
  },
  squidTour: {
    title: 'SquidTour - Platform Orientation',
    description: 'Take the Squidemy orientation tour. Learn how to navigate the platform and maximize your learning experience.',
    keywords: 'orientation, platform tour, getting started, onboarding'
  },
  squidsLife: {
    title: 'SquidsLife - Interactive Learning World',
    description: 'Explore the SquidsLife learning world. Complete challenges, earn rewards, and level up your skills.',
    keywords: 'gamified learning, interactive education, learning games, skill building'
  },
  squidLand: {
    title: 'SquidLand - Advanced Learning Zone',
    description: 'Enter SquidLand for advanced IT challenges and learning experiences.',
    keywords: 'advanced learning, IT challenges, skill advancement'
  },
  worlds: {
    title: 'Squid Worlds - Explore Learning Dimensions',
    description: 'Discover all the learning worlds in Squidemy. Each world offers unique challenges and opportunities.',
    keywords: 'learning worlds, gamified education, interactive learning'
  },
  leaderboard: {
    title: 'Leaderboard - Top Performers',
    description: 'See who\'s leading the pack! Check out the top performers in labs, CTF challenges, and overall learning progress.',
    keywords: 'leaderboard, top performers, rankings, competition, achievements'
  },
  
  // Blog & Content
  blog: {
    title: 'SquidNews - Tech Blog & Tutorials',
    description: 'Stay updated with the latest IT news, tutorials, and career tips. Learn from industry experts and advance your tech career.',
    keywords: 'tech blog, IT news, tech tutorials, career tips, cybersecurity news, cloud computing articles'
  },
  blogPost: {
    title: 'Blog Post',
    description: 'Read this article on Squidemy\'s tech blog.',
    keywords: 'tech article, IT blog, tutorial'
  },
  squidpedia: {
    title: 'SquidPedia - IT Knowledge Base',
    description: 'Your comprehensive IT knowledge base. Find definitions, explanations, and guides for all things tech.',
    keywords: 'IT knowledge base, tech glossary, definitions, guides, reference'
  },
  
  // Career & Jobs
  jobs: {
    title: 'IT Jobs - Find Your Next Role',
    description: 'Browse IT job listings curated for Squidemy learners. Find opportunities in cybersecurity, cloud, development, and more.',
    keywords: 'IT jobs, tech careers, job listings, cybersecurity jobs, developer jobs'
  },
  jobDetail: {
    title: 'Job Details',
    description: 'View job details and apply for this IT position.',
    keywords: 'job details, IT job, career opportunity'
  },
  internships: {
    title: 'IT Internships - Start Your Career',
    description: 'Find IT internship opportunities. Get real-world experience and jumpstart your tech career.',
    keywords: 'IT internships, tech internships, entry-level, career start'
  },
  careers: {
    title: 'Careers at Squidemy',
    description: 'Join the Squidemy team! View open positions and help us transform IT education.',
    keywords: 'squidemy careers, jobs at squidemy, tech jobs, education jobs'
  },
  interviewPrep: {
    title: 'Interview Prep - Ace Your Tech Interview',
    description: 'Prepare for IT job interviews with practice questions, tips, and mock interviews.',
    keywords: 'interview prep, tech interview, job interview, practice questions'
  },
  resumeLab: {
    title: 'Resume Lab - Build Your IT Resume',
    description: 'Create a professional IT resume. Get templates, tips, and AI-powered suggestions.',
    keywords: 'resume builder, IT resume, tech resume, CV creator'
  },
  resumeBuilder: {
    title: 'Resume Builder',
    description: 'Build your professional resume with our easy-to-use resume builder.',
    keywords: 'resume builder, CV builder, professional resume'
  },
  
  // Mentorship
  mentorship: {
    title: 'Mentorship Program - Learn from Experts',
    description: 'Connect with experienced IT professionals. Get personalized guidance and accelerate your career.',
    keywords: 'mentorship, IT mentor, career guidance, professional development'
  },
  mentorDirectory: {
    title: 'Mentor Directory - Find Your Mentor',
    description: 'Browse our directory of IT mentors. Find the perfect match for your learning goals.',
    keywords: 'mentor directory, find mentor, IT mentors, tech coaches'
  },
  mentorApplication: {
    title: 'Become a Mentor',
    description: 'Apply to become a Squidemy mentor. Share your expertise and help others grow.',
    keywords: 'become mentor, mentor application, teach IT'
  },
  
  // Shop & Marketplace
  shop: {
    title: 'SquidMerch - Official Merchandise',
    description: 'Shop official Squidemy merchandise. T-shirts, hoodies, stickers, and more. Show your squid pride!',
    keywords: 'squidemy merch, merchandise, t-shirts, hoodies, tech apparel'
  },
  cart: {
    title: 'Shopping Cart',
    description: 'Review your cart and checkout.',
    keywords: 'cart, checkout, shopping'
  },
  orders: {
    title: 'My Orders',
    description: 'View your order history and track shipments.',
    keywords: 'orders, order history, purchases'
  },
  vouchers: {
    title: 'Exam Vouchers - Certification Discounts',
    description: 'Get discounted exam vouchers for IT certifications. Save money on CompTIA, AWS, and other certification exams.',
    keywords: 'exam vouchers, certification discounts, CompTIA voucher, AWS voucher'
  },
  buyTokens: {
    title: 'Buy SquidCoins - Platform Currency',
    description: 'Purchase SquidCoins to unlock premium features, labs, and content.',
    keywords: 'buy tokens, squidcoins, platform currency, premium access'
  },
  
  // Membership & Support
  membership: {
    title: 'SquidMember - Premium Membership Plans',
    description: 'Unlock premium features with SquidMember. Get access to exclusive content, labs, mentorship, and more.',
    keywords: 'premium membership, subscription, exclusive content, premium features'
  },
  support: {
    title: 'Support - Help Center',
    description: 'Get help with your Squidemy account. Browse FAQs, submit tickets, and contact support.',
    keywords: 'support, help center, FAQ, customer service'
  },
  squidBug: {
    title: 'Report a Bug',
    description: 'Found a bug? Report it here and help us improve Squidemy.',
    keywords: 'bug report, feedback, issue report'
  },
  
  // User Account
  dashboard: {
    title: 'Dashboard - Your Learning Hub',
    description: 'Track your progress, view enrolled courses, and manage your learning journey.',
    keywords: 'dashboard, learning progress, enrolled courses'
  },
  profile: {
    title: 'My Profile',
    description: 'View and edit your Squidemy profile.',
    keywords: 'profile, account, user settings'
  },
  settings: {
    title: 'Account Settings',
    description: 'Manage your account settings, notifications, and preferences.',
    keywords: 'settings, account, preferences, notifications'
  },
  wishlist: {
    title: 'My Wishlist',
    description: 'View your saved courses and items.',
    keywords: 'wishlist, saved courses, favorites'
  },
  referrals: {
    title: 'Referral Program - Invite Friends',
    description: 'Invite friends to Squidemy and earn rewards. Share your referral link today!',
    keywords: 'referral program, invite friends, rewards, refer and earn'
  },
  
  // Community & Partnership
  donate: {
    title: 'Support Squidemy - Donate',
    description: 'Support Squidemy\'s mission to make IT education accessible. Your donation helps us create more free content.',
    keywords: 'donate, support, charity, IT education'
  },
  partnership: {
    title: 'Partner with Squidemy',
    description: 'Become a Squidemy partner. Collaborate with us on training, content, or business opportunities.',
    keywords: 'partnership, collaboration, business, sponsors'
  },
  schedule: {
    title: 'Schedule - Book a Session',
    description: 'Schedule mentoring sessions, consultations, or support calls.',
    keywords: 'schedule, booking, appointments, sessions'
  },
  
  // Tools & Resources
  itToolkit: {
    title: 'IT Toolkit - Essential Tools',
    description: 'Access essential IT tools and resources. Calculators, converters, and utilities for tech professionals.',
    keywords: 'IT tools, tech utilities, calculators, converters'
  },
  library: {
    title: 'Resource Library - Downloads & Materials',
    description: 'Access study materials, templates, and resources to support your learning.',
    keywords: 'resource library, study materials, downloads, templates'
  },
  downloads: {
    title: 'Downloads - Your Files',
    description: 'Access your downloaded materials and resources.',
    keywords: 'downloads, files, resources'
  },
  timeline: {
    title: 'Learning Timeline',
    description: 'View your learning timeline and track your progress over time.',
    keywords: 'timeline, progress, learning history'
  },
  
  // Legal & Privacy
  terms: {
    title: 'Terms of Service',
    description: 'Read Squidemy\'s terms of service and usage agreement.',
    keywords: 'terms of service, legal, agreement'
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'Learn how Squidemy protects your privacy and handles your data.',
    keywords: 'privacy policy, data protection, GDPR'
  },
  squidPrivacy: {
    title: 'Privacy Policy',
    description: 'Squidemy\'s comprehensive privacy policy.',
    keywords: 'privacy, data policy, user data'
  },
  squidCookies: {
    title: 'Cookie Policy',
    description: 'Learn about how Squidemy uses cookies.',
    keywords: 'cookies, cookie policy, tracking'
  },
  
  // Auth Pages (noindex)
  login: {
    title: 'Login to Squidemy',
    description: 'Sign in to your Squidemy account to continue learning.',
    keywords: 'login, sign in, account',
    noindex: true
  },

  // Events
  events: {
    title: 'Events - Live Workshops & Training Sessions',
    description: 'Join live IT training events, hands-on workshops, and expert-led sessions. Register for upcoming cybersecurity, Active Directory, and infrastructure events.',
    keywords: 'IT events, live workshops, training sessions, cybersecurity events, Active Directory training, hands-on labs'
  },
  eventDetail: {
    title: 'Event Details',
    description: 'View event details, schedule, and register for this live IT training session.',
    keywords: 'event details, workshop, live training, registration'
  },

  // Blackboard (LMS)
  blackboard: {
    title: 'Blackboard - Interactive Learning Management System',
    description: 'Access hands-on IT courses with interactive modules, quizzes, and labs. Master Active Directory, networking, cybersecurity, and more through guided learning paths.',
    keywords: 'LMS, blackboard, interactive courses, hands-on learning, Active Directory course, IT training modules'
  },
  blackboardCourse: {
    title: 'Course Classroom',
    description: 'Interactive course with modules, quizzes, and hands-on labs. Track your progress and earn certificates.',
    keywords: 'online course, interactive learning, modules, quizzes, labs, certificate'
  },
  confirmEmail: {
    title: 'Confirm Your Email',
    description: 'Confirm your email address to activate your Squidemy account.',
    keywords: 'confirm email, verify, activation',
    noindex: true
  },
  
  // Admin (noindex)
  admin: {
    title: 'Admin Panel',
    description: 'Squidemy administration panel.',
    keywords: 'admin',
    noindex: true
  },
  devPanel: {
    title: 'Developer Panel',
    description: 'Developer tools and settings.',
    keywords: 'developer',
    noindex: true
  },
  
  // Error Pages
  notFound: {
    title: '404 - Page Not Found',
    description: 'The page you\'re looking for doesn\'t exist. Return to Squidemy homepage.',
    keywords: '404, not found, error',
    noindex: true
  },
  maintenance: {
    title: 'Under Maintenance',
    description: 'Squidemy is currently under maintenance. We\'ll be back soon!',
    keywords: 'maintenance, down, unavailable',
    noindex: true
  }
};
